import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { AlertCircle, Sparkles, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';

const TutorRegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    bio: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { registerTutor } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await registerTutor({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        display_name: formData.display_name || formData.name,
        bio: formData.bio,
      });
      navigate('/tutor/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const setField = (k, v) => setFormData((s) => ({ ...s, [k]: v }));

  return (
    <div className="min-h-screen py-12 px-4" style={{ background: 'linear-gradient(135deg, #FEF3C7 0%, #FCE7F3 100%)' }}>
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-700 hover:text-slate-900 mb-6" data-testid="tutor-register-back">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="p-8 rounded-3xl shadow-lg border-2 border-slate-200">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 mb-2 text-amber-700">
                <Sparkles className="w-5 h-5" />
                <span className="text-sm font-semibold uppercase tracking-wide">Tutors' Treasure Box</span>
              </div>
              <h1 className="text-3xl font-bold text-slate-900">Register as a Tutor</h1>
              <p className="text-slate-600 mt-2">Sell your PSLE worksheets and keep 95% of every sale.</p>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 flex items-start gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="tr-name">Your Name</Label>
                <Input id="tr-name" data-testid="tutor-register-name" value={formData.name}
                  onChange={(e) => setField('name', e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="tr-display">Display Name (shown to buyers)</Label>
                <Input id="tr-display" data-testid="tutor-register-display" value={formData.display_name}
                  onChange={(e) => setField('display_name', e.target.value)} placeholder="e.g. Ms Tan (PSLE English)" />
              </div>
              <div>
                <Label htmlFor="tr-bio">Short Bio</Label>
                <Textarea id="tr-bio" data-testid="tutor-register-bio" value={formData.bio}
                  onChange={(e) => setField('bio', e.target.value)} placeholder="10+ years teaching P4-6 English…" rows={3} />
              </div>
              <div>
                <Label htmlFor="tr-email">Email</Label>
                <Input id="tr-email" data-testid="tutor-register-email" type="email" value={formData.email}
                  onChange={(e) => setField('email', e.target.value)} required />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="tr-pw">Password</Label>
                  <Input id="tr-pw" data-testid="tutor-register-password" type="password" value={formData.password}
                    onChange={(e) => setField('password', e.target.value)} required />
                </div>
                <div>
                  <Label htmlFor="tr-pw2">Confirm Password</Label>
                  <Input id="tr-pw2" data-testid="tutor-register-password2" type="password" value={formData.confirmPassword}
                    onChange={(e) => setField('confirmPassword', e.target.value)} required />
                </div>
              </div>

              <Button type="submit" disabled={loading} data-testid="tutor-register-submit"
                className="w-full bg-amber-600 hover:bg-amber-700 text-white h-11 rounded-xl font-semibold">
                {loading ? 'Creating account…' : 'Create free tutor account'}
              </Button>

              <p className="text-center text-sm text-slate-600">
                Already registered? <Link to="/login" className="text-amber-700 font-semibold hover:underline">Log in</Link>
              </p>
            </form>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default TutorRegisterPage;
